import { useRef, useState } from 'react';
import { ApiError } from '../../lib/api';
import { layersApi, pickFeatureAttachments } from '../../lib/layersApi';
import { mediaUrl } from '../../lib/mediaApi';

const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${bytes} Б`;
}

/** Any files attached to an object besides its one display photo (see
 * ObjectPhoto) — arbitrary type, up to 5 МБ each, any number. Stored under
 * properties._attachments (see layers.py's add/delete_feature_attachment —
 * gis_app has no DDL access for a real column/table here). */
export function FeatureAttachmentsSection({
  layerId,
  featureId,
  properties,
  canEdit,
  onChanged,
}: {
  layerId: string;
  featureId: string;
  properties: Record<string, unknown>;
  canEdit: boolean;
  onChanged: (properties: Record<string, unknown>) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const attachments = pickFeatureAttachments(properties);

  if (attachments.length === 0 && !canEdit) return null;

  const handleFile = async (file: File) => {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setError('Файл слишком большой (максимум 5 МБ).');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const updated = await layersApi.addFeatureAttachment(layerId, featureId, file);
      onChanged(updated.properties);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить файл.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId: string) => {
    setDeletingId(fileId);
    setError(null);
    try {
      const updated = await layersApi.deleteFeatureAttachment(layerId, featureId, fileId);
      onChanged(updated.properties);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить файл.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <p className="text-[11px] font-medium text-text-secondary">
        Файлы{attachments.length > 0 ? ` (${attachments.length})` : ''}
      </p>
      {attachments.length === 0 ? (
        <p className="mt-1.5 text-[13px] text-text-secondary">Нет</p>
      ) : (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {attachments.map((a) => (
            <div key={a.file_id} className="flex items-center gap-2">
              <a
                href={mediaUrl(a.file_id)}
                target="_blank"
                rel="noreferrer"
                title={a.name}
                className="min-w-0 flex-1 truncate text-[13px] font-medium text-action-primary hover:underline"
              >
                {a.name}
              </a>
              <span className="shrink-0 text-[11px] text-text-secondary">{formatFileSize(a.size_bytes)}</span>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleDelete(a.file_id)}
                  disabled={deletingId === a.file_id}
                  className="shrink-0 text-[11px] font-medium text-text-secondary hover:text-status-danger disabled:opacity-50"
                >
                  {deletingId === a.file_id ? '…' : 'Удалить'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        <>
          <input
            ref={inputRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) handleFile(file);
            }}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="mt-2 text-[12px] font-medium text-action-primary hover:underline disabled:opacity-50"
          >
            {uploading ? 'Загружаем…' : 'Добавить файл'}
          </button>
        </>
      )}
      {error && <p className="mt-1 text-[11px] font-medium text-status-danger">{error}</p>}
    </>
  );
}
